/**
 * Collision volume construction. ARCHITECTURE.md §4 (visual meshes are never gameplay
 * collision) and §15 (coordinate system).
 *
 * Four volume families, all capsules so a single distance test serves everything:
 *   pushbox   — one vertical capsule; keeps fighters from occupying the same space
 *   hurtbox   — legs / torso / head capsules; what an attack must touch to connect
 *   hitbox    — authored per move in fighter local space, transformed to world here
 *   throwbox  — a single sphere in front of the attacker
 *
 * Every volume is written into a preallocated record. Nothing here allocates after init.
 * Three-free.
 */

/** Indices into a fighter's hurtbox array. */
export const HURT_LEGS = 0;
export const HURT_TORSO = 1;
export const HURT_HEAD = 2;
export const HURTBOX_COUNT = 3;

/** A capsule record: segment from (ax,ay,az) to (bx,by,bz) with radius r. */
export function createCapsule() {
  return {
    ax: 0, ay: 0, az: 0,
    bx: 0, by: 0, bz: 0,
    r: 0,
    /** Diagnostic/label fields used by the debug overlay. */
    kind: '',
    owner: -1,
    active: false,
    /** Hit level this volume is vulnerable to / attacks with. */
    level: '',
    priority: 0,
    joint: '',
  };
}

export function setCapsule(c, ax, ay, az, bx, by, bz, r) {
  c.ax = ax; c.ay = ay; c.az = az;
  c.bx = bx; c.by = by; c.bz = bz;
  c.r = r;
  return c;
}

/**
 * Per-state hurtbox shape modifiers. `lo`/`hi` are fractions of the fighter's standing height;
 * `rMul` scales the capsule radius. Declaring these as data means a new state cannot silently
 * inherit a wrong hurtbox — it has to be added here.
 */
const HURT_PROFILES = {
  stand:  { legs: [0.06, 0.50, 1.00], torso: [0.48, 0.84, 1.10], head: [0.84, 0.99, 0.72] },
  crouch: { legs: [0.05, 0.34, 1.15], torso: [0.30, 0.56, 1.20], head: [0.54, 0.66, 0.75] },
  air:    { legs: [0.10, 0.48, 1.00], torso: [0.44, 0.80, 1.05], head: [0.78, 0.94, 0.72] },
  /** Face-down/face-up on the floor: a low, wide, mostly unhittable slab. */
  down:   { legs: [0.02, 0.14, 1.35], torso: [0.04, 0.20, 1.40], head: [0.10, 0.24, 0.85] },
  slump:  { legs: [0.03, 0.30, 1.15], torso: [0.26, 0.58, 1.15], head: [0.54, 0.70, 0.78] },
  /** Wall splat: stretched upright against the wall, fully exposed — the combo window. */
  splat:  { legs: [0.08, 0.52, 1.05], torso: [0.50, 0.88, 1.18], head: [0.86, 1.02, 0.78] },
};

/**
 * Chooses the hurtbox profile from the fighter's coarse posture. Physics deliberately does
 * not know the combat state vocabulary — `f.posture` is the contract (ARCHITECTURE.md §4).
 * @param {'stand'|'crouch'|'air'|'down'|'slump'|'splat'} posture
 */
export function hurtProfileFor(posture) {
  return HURT_PROFILES[posture] || HURT_PROFILES.stand;
}

/**
 * Rebuilds a fighter's three hurtbox capsules in world space.
 * @param {object} f FighterState
 * @param {Array<object>} out array of HURTBOX_COUNT capsule records
 */
export function buildHurtboxes(f, out) {
  const h = f.stats.height;
  const rBase = f.stats.pushboxRadius;
  const p = hurtProfileFor(f.posture);
  const x = f.x;
  const y = f.y;
  const z = f.z;

  // Down states lie along the combat axis rather than standing up in it.
  const prone = p === HURT_PROFILES.down;
  const lean = prone ? -f.facing * 0.34 : 0;

  writeHurt(out[HURT_LEGS], x, y, z, h, rBase, p.legs, lean, 'legs', f.index);
  writeHurt(out[HURT_TORSO], x, y, z, h, rBase, p.torso, lean * 1.6, 'torso', f.index);
  writeHurt(out[HURT_HEAD], x, y, z, h, rBase, p.head, lean * 2.2, 'head', f.index);
  return out;
}

function writeHurt(c, x, y, z, height, rBase, spec, lean, kind, owner) {
  const lo = spec[0] * height;
  const hi = spec[1] * height;
  setCapsule(c, x, y + lo, z, x + lean, y + hi, z, rBase * spec[2]);
  c.kind = kind;
  c.owner = owner;
  c.active = true;
  return c;
}

/** Rebuilds the vertical pushbox capsule. */
export function buildPushbox(f, out) {
  const r = f.stats.pushboxRadius;
  const top = f.crouching ? f.stats.crouchHeight : f.stats.pushboxHeight;
  // The pushbox segment sits inside the body so the capsule's caps produce the rounded
  // shoulder/hip separation players expect, rather than a hard cylinder.
  setCapsule(out, f.x, f.y + r, f.z, f.x, f.y + Math.max(r, top - r), f.z, r);
  out.kind = 'push';
  out.owner = f.index;
  out.active = true;
  return out;
}

/**
 * Transforms one authored hitbox from fighter local space into world space.
 * Local +X is forward, mirrored by `facing`. Local +Z is the fighter's left, which in world
 * space is `-facing * Z` so that a sidestep to the fighter's left is consistent for both sides.
 */
export function buildHitbox(f, hb, out) {
  const fx = f.facing;
  const ax = f.x + hb.ox * fx;
  const ay = f.y + hb.oy;
  const az = f.z + hb.oz * fx;
  const bx = f.x + hb.ex * fx;
  const by = f.y + hb.ey;
  const bz = f.z + hb.ez * fx;
  setCapsule(out, ax, ay, az, bx, by, bz, hb.r);
  out.kind = 'hit';
  out.owner = f.index;
  out.active = true;
  out.priority = hb.priority;
  out.joint = hb.joint;
  return out;
}

/** Transforms a throw box into world space as a sphere. */
export function buildThrowbox(f, tb, out) {
  const fx = f.facing;
  const x = f.x + tb.ox * fx;
  const y = f.y + tb.oy;
  const z = f.z + tb.oz * fx;
  setCapsule(out, x, y, z, x, y, z, tb.r);
  out.kind = 'throw';
  out.owner = f.index;
  out.active = true;
  out.joint = 'pelvis';
  return out;
}

/**
 * Which hurtbox regions a hit level is allowed to touch. This is what makes a low actually
 * whiff over a standing opponent's head rather than "hitting the legs but being blocked high".
 * @returns {number} bitmask over HURT_* indices
 */
export function levelHurtMask(level, defenderCrouching) {
  switch (level) {
    case 'low':
      return 1 << HURT_LEGS;
    case 'high':
      // A high whiffs entirely over a crouching opponent.
      return defenderCrouching ? 0 : (1 << HURT_TORSO) | (1 << HURT_HEAD);
    case 'mid':
    case 'special-mid':
    case 'unblockable':
      return (1 << HURT_LEGS) | (1 << HURT_TORSO) | (1 << HURT_HEAD);
    default:
      return 0;
  }
}

/** Axis-aligned bounds of a capsule, for cheap broad-phase rejection. */
export function capsuleBounds(c, out) {
  const r = c.r;
  out.minX = Math.min(c.ax, c.bx) - r;
  out.maxX = Math.max(c.ax, c.bx) + r;
  out.minY = Math.min(c.ay, c.by) - r;
  out.maxY = Math.max(c.ay, c.by) + r;
  out.minZ = Math.min(c.az, c.bz) - r;
  out.maxZ = Math.max(c.az, c.bz) + r;
  return out;
}

export function createBounds() {
  return { minX: 0, maxX: 0, minY: 0, maxY: 0, minZ: 0, maxZ: 0 };
}
